import { observer } from 'mobx-react-lite';
import { User, Check } from 'lucide-react';
import { collaboratorsByCompany } from '../../model/professional/compagny-model';
import { t } from 'i18next';

interface CollaboratorSelectorProps {
  collaborators: collaboratorsByCompany[];
  selectedIds: string[];
  onToggle: (collaboratorId: string) => void;
  disabled?: boolean;
  label?: string;
}

const CollaboratorSelector = observer((props: CollaboratorSelectorProps) => {
  const { collaborators, selectedIds, onToggle, disabled = false, label } = props;
  const translate = t;

  if (!collaborators || collaborators.length === 0) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 py-2">
        {translate('services.noCollaboratorsAvailable')}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {collaborators.map((collaborator) => {
          const isSelected = selectedIds.includes(collaborator.collaboratorCompagnieId);

          return (
            <button
              key={collaborator.collaboratorCompagnieId}
              type="button"
              onClick={() => !disabled && onToggle(collaborator.collaboratorCompagnieId)}
              disabled={disabled}
              className={`
                flex items-center justify-between p-3 rounded-lg border transition-all min-w-0
                ${isSelected
                  ? 'border-primary bg-primary/10 dark:border-primary-dark dark:bg-primary-dark/10'
                  : 'border-gray-300 dark:border-gray-600 hover:border-primary dark:hover:border-primary-dark'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
                  ${isSelected
                    ? 'bg-primary text-white dark:bg-primary-dark'
                    : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                  }
                `}>
                  {collaborator.avatar ? (
                    <img
                      src={collaborator.avatar}
                      alt={`${collaborator.firstName} ${collaborator.lastName}`}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <User size={18} />
                  )}
                </div>
                <div className="text-left min-w-0 flex-1">
                  <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                    {collaborator.firstName}
                  </div>
                  <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                    {collaborator.lastName}
                  </div>
                  {collaborator.profession && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {collaborator.profession}
                    </div>
                  )}
                </div>
              </div>
              {isSelected && (
                <div className="flex-shrink-0 ml-2">
                  <Check size={18} className="text-primary dark:text-primary-dark" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
});

export default CollaboratorSelector;
